import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import SportsHockeyIcon from "@mui/icons-material/SportsHockey";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { fireConfetti } from "../../utils/fireConfetti";
import { getMyLeagues, setLeagueArchived } from "../../services/api/leagues";
import { verifyCheckoutSession, fetchMySubscription } from "../../services/api/subscriptions";
import { hasLocallyValidSession } from "../../services/auth/auth-session";
import { PLAN_DEFINITIONS } from "../../config/plans";
import QrCodeDialog from "../../components/QrCodeDialog";
import ParticipantListDialog from "../../components/ParticipantListDialog";
import LeagueGameProgress from "../../components/LeagueGameProgress";
import { loadErrorMessage } from "../../utils/api-error";

const ORGANIZER_ROLES = new Set(["owner", "admin", "treasurer", "coach"]);

const SKILL_LEVEL_LABELS = {
  A: "A",
  A_B: "A/B",
  B: "B",
  B_C: "B/C",
  C_BEGINNER: "C / Beginner",
  ALL_WELCOME: "All welcome",
};

const formatGameDate = (iso, withTime) =>
  new Date(iso).toLocaleString(
    [],
    withTime
      ? { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }
      : { month: "short", day: "numeric", year: "numeric" },
  );

/** Soonest next game first, then leagues with nothing coming up, then inactive ones, and archived ones last. */
function leagueRank(l) {
  if (l.isArchived) return 3;
  if (l.isInactive) return 2;
  return l.nextGameAt ? 0 : 1;
}
const compareLeagues = (a, b) => {
  const byRank = leagueRank(a) - leagueRank(b);
  if (byRank !== 0) return byRank;
  if (a.nextGameAt && b.nextGameAt) return new Date(a.nextGameAt) - new Date(b.nextGameAt);
  return (a.name ?? "").localeCompare(b.name ?? "");
};

export default function MyLeaguesView() {
  const navigate = useNavigate();
  const location = useLocation();
  const [leagues, setLeagues] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [qrLeague, setQrLeague] = React.useState(null);
  const [participantsLeague, setParticipantsLeague] = React.useState(null);
  const [welcomeOpen, setWelcomeOpen] = React.useState(false);
  const [welcomePlan, setWelcomePlan] = React.useState(null);
  const [subscription, setSubscription] = React.useState(null);

  // Which leagues to show. The defaults hide leagues that have gone quiet (no game in over 30 days and none coming
  // up) and leagues the person archived; these switches bring them back. Kept in the address so a refresh stays put.
  const [searchParams, setSearchParams] = useSearchParams();
  const showInactive = searchParams.get("inactive") === "1";
  const showArchived = searchParams.get("archived") === "1";
  const setFilter = (key, on) => {
    const next = new URLSearchParams(searchParams);
    if (on) next.set(key, "1");
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  // Archiving a league: which one is being confirmed, which request is running, and the note afterwards.
  const [archiveTarget, setArchiveTarget] = React.useState(null);
  const [archivingId, setArchivingId] = React.useState(null);
  const [notice, setNotice] = React.useState(null); // { text, undo? }


  React.useEffect(() => {
    if (!hasLocallyValidSession()) {
      navigate("/pricing", { replace: true });
      return;
    }
    Promise.allSettled([getMyLeagues(), fetchMySubscription()])
      .then(([leaguesResult, subResult]) => {
        let loadedLeagues = [];
        if (leaguesResult.status === "fulfilled") {
          loadedLeagues = Array.isArray(leaguesResult.value) ? leaguesResult.value : [];
          setLeagues(loadedLeagues);
        } else {
          const msg = leaguesResult.reason?.message || "";
          if (!msg.includes("Authentication required")) {
            setError(loadErrorMessage(leaguesResult.reason, "Something went wrong loading your leagues. Please refresh and try again."));
          }
        }
        const sub = subResult.status === "fulfilled" ? subResult.value : null;
        // Someone with neither a subscription nor any league has nothing to manage here, so Pricing is the right
        // place. Players (no subscription, but in a league) stay: this is where they archive leagues.
        if (!sub && loadedLeagues.length === 0) {
          navigate("/pricing", { replace: true });
          return;
        }
        setSubscription(sub);
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isCheckoutSuccess = params.get("checkout") === "success";
    const sessionId = params.get("session_id");

    if (!isCheckoutSuccess || !sessionId) return;

    // Strip the query params from the URL immediately so a refresh doesn't re-trigger
    window.history.replaceState({}, "", location.pathname);

    verifyCheckoutSession(sessionId)
      .then((result) => {
        if (!result?.paid) return;
        const plan = PLAN_DEFINITIONS.find((p) => p.id === result.planId) ?? null;
        setWelcomePlan(plan);
        setWelcomeOpen(true);
        fireConfetti();
      })
      .catch(() => {
        // Silently ignore — don't block the page if verification fails
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patchLeague = (leagueId, changes) =>
    setLeagues((list) => list.map((l) => (l.id === leagueId ? { ...l, ...changes } : l)));

  const changeArchived = async (league, archived) => {
    setArchivingId(league.id);
    setError("");
    try {
      const result = await setLeagueArchived(league.id, archived);
      patchLeague(league.id, { isArchived: result.archived, archivedAt: result.archivedAt });
      setNotice(
        archived
          ? {
              text: `Archived ${league.name}. You won't get emails from it, and it's tucked away until you turn on "Show archived leagues".`,
              undo: () => changeArchived(league, false),
            }
          : { text: `Unarchived ${league.name}. Its emails are back on.` },
      );
    } catch (err) {
      setError(loadErrorMessage(err, err instanceof Error ? err.message : "Could not update that league. Please try again."));
    } finally {
      setArchivingId(null);
      setArchiveTarget(null);
    }
  };

  const hiddenInactiveCount = leagues.filter((l) => !l.isArchived && l.isInactive).length;
  const archivedCount = leagues.filter((l) => l.isArchived).length;
  const visibleLeagues = leagues
    .filter((l) => (l.isArchived ? showArchived : !l.isInactive || showInactive))
    .sort(compareLeagues);

  const leagueTypeLabel = (type) => (type === "COMPETITIVE" ? "Competitive" : "Pickup");
  const skillLevelLabel = (level) => SKILL_LEVEL_LABELS[level] ?? level ?? "—";

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {qrLeague && (
        <QrCodeDialog
          open={!!qrLeague}
          onClose={() => setQrLeague(null)}
          title={qrLeague.name}
          subtitle="Scan to join this league"
          url={`${window.location.origin}/leagues/${qrLeague.id}/signup`}
        />
      )}

      <ParticipantListDialog
        open={!!participantsLeague}
        onClose={() => setParticipantsLeague(null)}
        leagueId={participantsLeague?.id}
        leagueName={participantsLeague?.name}
      />

      <Dialog open={Boolean(archiveTarget)} onClose={() => (archivingId ? null : setArchiveTarget(null))}>
        <DialogTitle>Archive {archiveTarget?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            It moves out of your list and you stop getting emails from it, apart from receipts for payments you make. You
            stay signed up for any games you&apos;ve registered for. You can bring it back any time: turn on &ldquo;Show archived
            leagues&rdquo; and choose Unarchive.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveTarget(null)} disabled={Boolean(archivingId)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => changeArchived(archiveTarget, true)} disabled={Boolean(archivingId)}>
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={welcomeOpen} maxWidth="xs" fullWidth>
        <DialogContent>
          <Stack spacing={3} alignItems="center" sx={{ py: 2, textAlign: "center" }}>
            <SportsHockeyIcon sx={{ fontSize: 56, color: "#C8102E" }} />
            <Typography variant="h5" fontWeight={700}>
              You&apos;re in!
            </Typography>
            {welcomePlan && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: -1.5 }}>
                You&apos;re now on the <strong>{welcomePlan.name}</strong> plan
                {welcomePlan.monthlyPrice === 0 ? " — free, forever." : "."}
              </Typography>
            )}
            <Typography variant="body1" color="text.secondary">
              Lace &apos;em up — your Puck-Pickup league is ready to drop the puck.
              Set up your first league and get your crew on the schedule.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              No penalties for beginners. We promise.
            </Typography>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => { setWelcomeOpen(false); navigate("/new-league"); }}
              sx={{ mt: 1, fontWeight: 700, fontSize: "1.05rem" }}
            >
              Let&apos;s Go!
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" component="h3">
              My leagues
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Leagues you run or play in.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {leagues.some((l) => ORGANIZER_ROLES.has(l.userRole)) && (
              <Button variant="outlined" onClick={() => navigate("/create-game")}>
                Create Game
              </Button>
            )}
            {/* Creating leagues needs a subscription. Players without one use this page only to see and archive the
                leagues they play in, so they don't get the button. */}
            {subscription
              ? (() => {
                  const isLaunchTier = subscription.planId === "launch";
                  const hasOrganizerLeague = leagues.some((l) => ORGANIZER_ROLES.has(l.userRole));
                  const atLeagueLimit = isLaunchTier && hasOrganizerLeague;
                  return (
                    <Tooltip
                      title={atLeagueLimit ? "Upgrade your plan to create additional leagues." : ""}
                      disableHoverListener={!atLeagueLimit}
                    >
                      <span>
                        <Button
                          variant="contained"
                          disabled={atLeagueLimit}
                          onClick={() => navigate("/new-league")}
                        >
                          New League
                        </Button>
                      </span>
                    </Tooltip>
                  );
                })()
              : null}
          </Stack>
        </Stack>

        {error ? (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        {notice ? (
          <Alert
            severity="success"
            onClose={() => setNotice(null)}
            action={
              notice.undo ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    const undo = notice.undo;
                    setNotice(null);
                    undo();
                  }}
                >
                  Undo
                </Button>
              ) : null
            }
          >
            {notice.text}
          </Alert>
        ) : null}

        {!loading && (hiddenInactiveCount > 0 || showInactive || archivedCount > 0 || showArchived) ? (
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap role="group" aria-label="Filter leagues">
            {hiddenInactiveCount > 0 || showInactive ? (
              <FormControlLabel
                control={<Switch checked={showInactive} onChange={(e) => setFilter("inactive", e.target.checked)} />}
                label={`Show inactive leagues${hiddenInactiveCount > 0 ? ` (${hiddenInactiveCount})` : ""}`}
              />
            ) : null}
            {archivedCount > 0 || showArchived ? (
              <FormControlLabel
                control={<Switch checked={showArchived} onChange={(e) => setFilter("archived", e.target.checked)} />}
                label={`Show archived leagues${archivedCount > 0 ? ` (${archivedCount})` : ""}`}
              />
            ) : null}
          </Stack>
        ) : null}

        {loading ? (
          <Typography color="text.secondary">Loading…</Typography>
        ) : leagues.length === 0 && !error ? (
          <Box textAlign="center" sx={{ py: 8 }}>
            <SportsHockeyIcon sx={{ fontSize: 52, color: "text.disabled", mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No leagues yet
            </Typography>
            <Typography color="text.secondary" sx={{ mb: subscription ? 3 : 0 }}>
              {subscription ? "Create your first league to get your crew on the ice." : "Leagues you play in will show up here."}
            </Typography>
            {subscription ? (
              <Button variant="contained" onClick={() => navigate("/new-league")}>
                Create a League
              </Button>
            ) : null}
          </Box>
        ) : visibleLeagues.length === 0 ? (
          <Box textAlign="center" sx={{ py: 6 }}>
            <SportsHockeyIcon sx={{ fontSize: 52, color: "text.disabled", mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No active leagues right now
            </Typography>
            <Typography color="text.secondary">
              {hiddenInactiveCount > 0 && archivedCount > 0
                ? "Your other leagues are inactive or archived. Use the switches above to see them."
                : hiddenInactiveCount > 0
                  ? "Your other leagues are inactive (no games in the last 30 days). Turn on “Show inactive leagues” to see them."
                  : "Your other leagues are archived. Turn on “Show archived leagues” to see them."}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {visibleLeagues.map((league) => (
              <Card key={league.id} variant="outlined" sx={{ opacity: league.isArchived ? 0.7 : 1 }}>
                <CardContent>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ sm: "center" }}
                    gap={2}
                  >
                    <Box
                      sx={{ cursor: ORGANIZER_ROLES.has(league.userRole) ? "pointer" : "default", flex: 1 }}
                      onClick={() => {
                        if (ORGANIZER_ROLES.has(league.userRole)) navigate(`/manage-league/${league.id}`);
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="h6">{league.name}</Typography>
                        {league.isArchived ? <Chip size="small" label="Archived" /> : null}
                        {!league.isArchived && league.isInactive ? <Chip size="small" label="Inactive" variant="outlined" /> : null}
                      </Stack>
                      {(league.city || league.state) && (
                        <Typography variant="body2" color="text.secondary">
                          {[league.city, league.state].filter(Boolean).join(", ")}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {leagueTypeLabel(league.leagueType)}
                        {" · "}
                        {skillLevelLabel(league.skillLevel)}
                        {league.plan?.displayName ? ` · ${league.plan.displayName}` : ""}
                        {league.uniquePlayerCount != null
                          ? ` · ${league.uniquePlayerCount} player${league.uniquePlayerCount !== 1 ? "s" : ""}`
                          : ""}
                        {league.gameRegistrationCount != null
                          ? ` · ${league.gameRegistrationCount} game registration${league.gameRegistrationCount !== 1 ? "s" : ""}`
                          : ""}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {league.nextGameAt
                            ? `Next game: ${formatGameDate(league.nextGameAt, true)}`
                            : league.lastGameAt
                              ? `Last game: ${formatGameDate(league.lastGameAt, false)}`
                              : "No games scheduled yet"}
                        </Typography>
                        {league.nextGameAt && league.nextGameId ? (
                          <Button
                            size="small"
                            variant="outlined"
                            // A touch smaller than the card's other buttons: it's a quick link beside the date.
                            sx={{ py: 0, px: 1, minHeight: 24, minWidth: 0, fontSize: "0.7rem", lineHeight: 1.7 }}
                            aria-label={`Game details for ${league.name}'s next game`}
                            onClick={(e) => {
                              // This sits inside the area that opens the manage page for organizers; don't also do that.
                              e.stopPropagation();
                              navigate(`/games/${league.nextGameId}/roster`);
                            }}
                          >
                            Game details
                          </Button>
                        ) : null}
                      </Stack>
                    </Box>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexShrink={0}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<QrCode2Icon />}
                        onClick={() => setQrLeague(league)}
                        sx={{ flexBasis: { xs: "calc(50% - 4px)", sm: "auto" } }}
                      >
                        Share signup
                      </Button>
                      {league.userRole !== "owner" ? (
                        <Button
                          size="small"
                          variant="text"
                          color="inherit"
                          disabled={archivingId === league.id}
                          onClick={() => (league.isArchived ? changeArchived(league, false) : setArchiveTarget(league))}
                          sx={{ flexBasis: { xs: "calc(50% - 4px)", sm: "auto" } }}
                        >
                          {league.isArchived ? "Unarchive" : "Archive"}
                        </Button>
                      ) : null}
                      {ORGANIZER_ROLES.has(league.userRole) && (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setParticipantsLeague(league)}
                            sx={{ flexBasis: { xs: "calc(50% - 4px)", sm: "auto" } }}
                          >
                            Participants
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              navigate("/create-game", { state: { leagueId: league.id } })
                            }
                            sx={{ flexBasis: { xs: "calc(50% - 4px)", sm: "auto" } }}
                          >
                            + Game
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(`/manage-league/${league.id}`)}
                            sx={{ flexBasis: { xs: "calc(50% - 4px)", sm: "auto" } }}
                          >
                            Manage
                          </Button>
                        </>
                      )}
                    </Stack>
                  </Stack>
                  {/* For anyone signed up for games in this league: how many, and whether more are open. */}
                  <LeagueGameProgress league={league} onSignUp={() => navigate(`/leagues/${league.id}/signup`)} />
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}
